import { formatearMoneda, horaLocal, type ReembolsoTarjetaResumen, type VentaVista } from '@barbones/shared';
import { PencilLine, Printer, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Insignia } from '@/components/ui/data';
import { Dialogo } from '@/components/ui/overlay';
import { cn } from '@/lib/utils';
import { CorreccionCobroDialog, etiquetaPago } from './CorreccionCobroDialog';
import { useReintentarReembolso } from './datos';
import { imprimirTicket } from './imprimir';

/** Motivo por el que una venta del día no admite corrección, o null si se puede corregir. */
export function bloqueoCorreccion(venta: VentaVista): string | null {
  if (venta.estado !== 'pagada') return 'Solo se corrigen ventas pagadas.';
  if (venta.pagos.some((p) => p.metodo === 'tarjeta')) return 'Las ventas cobradas con tarjeta no se corrigen desde aquí.';
  return null;
}

/** Pestaña «Del día»: ventas cobradas o canceladas hoy, con detalle y corrección del cobro. */
export function VentasDelDia({ ventas, puedeCorregir, puedeReintentar = false }: { ventas: VentaVista[]; puedeCorregir: boolean; puedeReintentar?: boolean }) {
  const [seleccionadaId, setSeleccionadaId] = useState<string | null>(null);
  const [corrigiendo, setCorrigiendo] = useState<VentaVista | null>(null);
  // Se deriva de la lista para reflejar los cambios (p. ej. el reembolso reintentado).
  const seleccionada = ventas.find((v) => v.id === seleccionadaId) ?? null;

  if (ventas.length === 0) return <p className="px-4 py-6 text-center text-[13px] text-foreground-muted">Aún no hay ventas cobradas hoy.</p>;

  return (
    <>
      <ul className="flex flex-col p-2">
        {ventas.map((v) =>
          v.estado === 'pagada' ? (
            <li key={v.id} className="flex items-center gap-1 border-b border-border last:border-0">
              <button
                type="button"
                onClick={() => setSeleccionadaId(v.id)}
                className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-3 py-2 text-left text-[13px] hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                aria-label={`Venta ${v.folio}, ${v.cliente?.nombre ?? 'Mostrador'}, ${formatearMoneda(v.total)}`}
              >
                <span className="cifras w-10 shrink-0 text-foreground-muted">#{v.folio}</span>
                <span className="flex-1 truncate">{v.cliente?.nombre ?? 'Mostrador'}</span>
                {v.reembolsosTarjeta.some((r) => r.estado === 'error') && <Insignia tono="peligro">Reembolso fallido</Insignia>}
                {v.ajusteCorreccion !== 0 && <Insignia tono="neutro">Corregida</Insignia>}
                <span className="cifras">{formatearMoneda(v.total)}</span>
              </button>
              <Button variante="fantasma" tamano="icono" onClick={() => void imprimirTicket(v.id)} aria-label={`Imprimir venta ${v.folio}`}>
                <Printer />
              </Button>
            </li>
          ) : (
            <li key={v.id} className="flex items-center gap-2 border-b border-border px-3 py-2 text-[13px] last:border-0">
              <span className="cifras w-10 text-foreground-muted">#{v.folio}</span>
              <span className="flex-1 truncate text-foreground-muted">{v.cliente?.nombre ?? 'Mostrador'}</span>
              <Insignia tono="neutro">Cancelada</Insignia>
            </li>
          ),
        )}
      </ul>

      {seleccionada && (
        <DetalleVentaDialogo
          venta={seleccionada}
          puedeCorregir={puedeCorregir}
          puedeReintentar={puedeReintentar}
          onCerrar={() => setSeleccionadaId(null)}
          onCorregir={() => {
            setCorrigiendo(seleccionada);
            setSeleccionadaId(null);
          }}
        />
      )}

      {corrigiendo && <CorreccionCobroDialog venta={corrigiendo} abierto onCerrar={() => setCorrigiendo(null)} />}
    </>
  );
}

/** Detalle de una venta cobrada o cancelada: conceptos, pagos, reembolsos a tarjeta, imprimir y corregir. */
export function DetalleVentaDialogo({
  venta,
  puedeCorregir,
  puedeReintentar,
  reembolsoResaltado,
  onCerrar,
  onCorregir,
}: {
  venta: VentaVista;
  puedeCorregir: boolean;
  puedeReintentar: boolean;
  reembolsoResaltado?: string;
  onCerrar: () => void;
  onCorregir?: () => void;
}) {
  const bloqueo = bloqueoCorreccion(venta);
  return (
    <Dialogo
      abierto
      onCambiarAbierto={(a) => !a && onCerrar()}
      titulo={`Venta ${venta.folio}`}
      descripcion={`${venta.cliente?.nombre ?? 'Mostrador'}${venta.pagadaEn ? ` · ${horaLocal(new Date(venta.pagadaEn))}` : ''}`}
      pie={
        <>
          <Button variante="secundario" onClick={() => void imprimirTicket(venta.id)}>
            <Printer /> Imprimir
          </Button>
          {puedeCorregir && !bloqueo && onCorregir && (
            <Button onClick={onCorregir}>
              <PencilLine /> Corregir venta
            </Button>
          )}
        </>
      }
    >
      <DetalleVenta venta={venta} />
      {venta.reembolsosTarjeta.length > 0 && <ReembolsosTarjeta venta={venta} puedeReintentar={puedeReintentar} resaltado={reembolsoResaltado} />}
      {puedeCorregir && bloqueo && <p className="mt-3 rounded-md bg-muted px-3 py-2 text-[12.5px] text-foreground-muted">{bloqueo}</p>}
    </Dialogo>
  );
}

const ESTADO_REEMBOLSO: Record<ReembolsoTarjetaResumen['estado'], { texto: string; tono: 'info' | 'exito' | 'peligro' }> = {
  pendiente: { texto: 'En proceso', tono: 'info' },
  aplicado: { texto: 'Aplicado', tono: 'exito' },
  error: { texto: 'Falló', tono: 'peligro' },
};

/** Estado de los reembolsos a tarjeta de la venta; con error, «Reintentar» para quien resuelve aprobaciones. */
function ReembolsosTarjeta({ venta, puedeReintentar, resaltado }: { venta: VentaVista; puedeReintentar: boolean; resaltado?: string }) {
  const reintentar = useReintentarReembolso();
  return (
    <section aria-label="Reembolso a tarjeta" className="mt-3 flex flex-col gap-2 border-t border-border pt-2">
      <h3 className="text-[12px] font-medium uppercase tracking-wide text-foreground-muted">Reembolso a tarjeta</h3>
      {venta.reembolsosTarjeta.map((r) => {
        const estado = ESTADO_REEMBOLSO[r.estado];
        return (
          <div key={r.id} className={cn('rounded-md px-3 py-2 text-[13px]', r.id === resaltado ? 'bg-accent-soft ring-2 ring-accent/25' : 'bg-muted')}>
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2">
                <Insignia tono={estado.tono}>{estado.texto}</Insignia>
                <span className="text-foreground-muted">{horaLocal(new Date(r.creadoEn))}</span>
              </span>
              <span className="cifras">{formatearMoneda(r.montoMXN)}</span>
            </div>
            {r.estado === 'error' && (
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <p className="text-[12.5px] text-foreground-muted">
                  Mercado Pago no aplicó el reembolso{r.codigoError ? ` (${r.codigoError})` : ''}.{' '}
                  {puedeReintentar ? 'Reinténtalo o verifícalo en tu panel de Mercado Pago.' : 'Pide al dueño que lo reintente.'}
                </p>
                {puedeReintentar && (
                  <Button
                    variante="secundario"
                    tamano="sm"
                    cargando={reintentar.isPending && reintentar.variables === r.id}
                    disabled={reintentar.isPending}
                    onClick={() => reintentar.mutate(r.id)}
                  >
                    <RotateCcw /> Reintentar
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

function DetalleVenta({ venta }: { venta: VentaVista }) {
  const fila = 'flex justify-between gap-3 py-1 text-[13px]';
  return (
    <div className="flex flex-col gap-3">
      <section aria-label="Conceptos">
        {venta.lineas.map((l) => (
          <div key={l.id} className={fila}>
            <span className="min-w-0 truncate">
              {l.cantidad > 1 ? `${l.cantidad}× ` : ''}
              {l.descripcion}
              {l.comisionista && <span className="text-foreground-muted"> · {l.comisionista.nombre}</span>}
            </span>
            <span className="cifras">{formatearMoneda(l.neto)}</span>
          </div>
        ))}
      </section>
      <section aria-label="Totales" className="border-t border-border pt-2">
        {venta.descuento > 0 && (
          <div className={fila}>
            <span className="text-foreground-muted">Descuento</span>
            <span className="cifras">-{formatearMoneda(venta.descuento)}</span>
          </div>
        )}
        {venta.ajusteCorreccion !== 0 && (
          <div className={fila}>
            <span className="text-foreground-muted">Corrección</span>
            <span className="cifras">
              {venta.ajusteCorreccion > 0 ? '+' : '-'}
              {formatearMoneda(Math.abs(venta.ajusteCorreccion))}
            </span>
          </div>
        )}
        <div className={`${fila} font-semibold`}>
          <span>Total</span>
          <span className="cifras">{formatearMoneda(venta.total)}</span>
        </div>
      </section>
      <section aria-label="Pagos" className="border-t border-border pt-2">
        {venta.pagos.map((p) => (
          <div key={p.id} className={fila}>
            <span>
              {etiquetaPago(p.metodo, p.moneda)}
              {p.referencia && <span className="text-foreground-muted"> · {p.referencia}</span>}
            </span>
            <span className="cifras">{formatearMoneda(p.monto, p.moneda)}</span>
          </div>
        ))}
        {venta.cambioMXN > 0 && (
          <div className={fila}>
            <span className="text-foreground-muted">Cambio</span>
            <span className="cifras">{formatearMoneda(venta.cambioMXN)}</span>
          </div>
        )}
      </section>
      {venta.cobradaPor && <p className="text-[12.5px] text-foreground-muted">Cobró: {venta.cobradaPor}</p>}
    </div>
  );
}
